import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface DimensionCardProps {
  title: string;
  icon: LucideIcon;
  colorTheme: string;
  defaultExpanded?: boolean;
  id?: string;
  children: React.ReactNode;
}

const themeStyles: Record<string, { header: string; border: string; iconColor: string }> = {
  red: {
    header: "bg-red-50 dark:bg-red-900/20",
    border: "border-red-200 dark:border-red-800/40",
    iconColor: "text-red-600 dark:text-red-400",
  },
  blue: {
    header: "bg-blue-50 dark:bg-blue-900/20",
    border: "border-blue-200 dark:border-blue-800/40",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  green: {
    header: "bg-green-50 dark:bg-green-900/20",
    border: "border-green-200 dark:border-green-800/40",
    iconColor: "text-green-600 dark:text-green-400",
  },
  yellow: {
    header: "bg-yellow-50 dark:bg-yellow-900/20",
    border: "border-yellow-200 dark:border-yellow-800/40",
    iconColor: "text-yellow-600 dark:text-yellow-400",
  },
  purple: {
    header: "bg-purple-50 dark:bg-purple-900/20",
    border: "border-purple-200 dark:border-purple-800/40",
    iconColor: "text-purple-600 dark:text-purple-400",
  },
};

export function DimensionCard({
  title,
  icon: Icon,
  colorTheme,
  defaultExpanded = true,
  id,
  children,
}: DimensionCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = themeStyles[colorTheme] ?? themeStyles.blue;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div id={id} className={`rounded-lg border ${theme.border} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-4 py-3 ${theme.header}`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${theme.iconColor}`} />
          <span className={`font-semibold text-sm ${theme.iconColor}`}>{title}</span>
        </div>
        <Chevron className="h-4 w-4 text-gray-400" />
      </button>
      {expanded && (
        <div className="p-4">
          {children}
        </div>
      )}
    </div>
  );
}
